import type { LivePreviewConfig } from 'payload';
import type { AbTest, Page } from '../payload-types';

type LivePreviewURLArgs = Parameters<Extract<LivePreviewConfig['url'], (...args: never[]) => unknown>>[0];

export const AB_PREVIEW_QUERY_PARAM = 'abPreviewPage';

export const resolveRelationId = (relation: unknown): string | undefined =>
  typeof relation === 'string' ? relation : (relation as { id?: string } | undefined)?.id;

export const getAbPageIds = (data: Pick<AbTest, 'control' | 'variants'>): string[] => {
  const controlId = resolveRelationId(data.control);
  const variantIds = (data.variants || []).map((v) => resolveRelationId(v.target));
  return [...new Set([controlId, ...variantIds])].filter((id): id is string => !!id);
};

export const extractAbPreviewParam = (url: string | null): string | undefined => {
  try {
    return new URL(url!).searchParams.get(AB_PREVIEW_QUERY_PARAM) || undefined;
  } catch {
    return;
  }
};

export const resolvePreviewPageId = (
  abData: Pick<AbTest, 'control' | 'variants'>,
  selectedPageId: string | undefined,
): string | undefined => {
  const validIds = new Set(getAbPageIds(abData));
  const controlId = resolveRelationId(abData.control);

  const candidate = selectedPageId || controlId;
  return candidate && validIds.has(candidate) ? candidate : controlId;
};

export const resolveAbPreview = async (args: LivePreviewURLArgs): Promise<Page | undefined> => {
  const { collectionConfig, data, req } = args;

  if (collectionConfig?.slug !== 'ab_test') return;

  const abData = data as AbTest;

  const selectedPageId =
    abData._previewPageId || extractAbPreviewParam(req.headers.get('referer')) || extractAbPreviewParam(req.headers.get('x-forwarded-url'));

  const pageId = resolvePreviewPageId(abData, selectedPageId);

  if (!pageId) return;

  try {
    return (await req.payload.findByID({
      collection: 'pages',
      id: pageId,
      locale: req.locale,
    })) as Page;
  } catch {
    return;
  }
};
